import {
  ConfirmationMessageTypeEnum,
  StreamConfirmationPartTypeName,
  TerraUIMessageDataPartName,
  type BaseDataMessage,
  type HITLActionRequest,
  type HITLDecision,
  type HITLRequestConfirmationDecision,
  type HITLRequestConfirmationMessage,
  type TerraUIMessage,
  type TerraUIMessageDataPart,
  type TerraUIMessagePart,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import { z } from 'zod';

type DataPartLike = { type: string; data?: BaseDataMessage };

type ConfirmationScanEntry =
  | { kind: 'confirmation'; confirmation: UserConfirmationMessage }
  | { kind: 'hitl-request' };

export type HITLConfirmationDecisionResult = {
  decision: HITLRequestConfirmationDecision | undefined;
  decisions: HITLDecision[] | undefined;
};

type ConfirmationSearch = {
  messages: TerraUIMessage[];
  startIndex: number;
  targetMessageIds: string[];
};

const UserConfirmationMetadataSchema = z.looseObject({
  type: z.literal(ConfirmationMessageTypeEnum.UserConfirmation),
});

const BaseDataMessageSchema = z.looseObject({
  type: z.string(),
});

const EMPTY_DECISION_RESULT: HITLConfirmationDecisionResult = {
  decision: undefined,
  decisions: undefined,
};

export function isDataOrConfirmationPart(
  part: TerraUIMessagePart,
): part is TerraUIMessageDataPart & DataPartLike {
  return (
    part.type === TerraUIMessageDataPartName ||
    part.type === StreamConfirmationPartTypeName
  );
}

export function getMetadataAsUserConfirmation(
  metadata: unknown,
): UserConfirmationMessage | undefined {
  const result = UserConfirmationMetadataSchema.safeParse(metadata);

  if (!result.success) return undefined;

  return metadata as UserConfirmationMessage;
}

export function isUserConfirmationData(
  data: BaseDataMessage,
): data is UserConfirmationMessage {
  return data.type === ConfirmationMessageTypeEnum.UserConfirmation;
}

const hasHitlRequestPrompt = (data: BaseDataMessage): boolean => {
  const prompt = (data as { hitlRequestConfirmationPrompt?: unknown })
    .hitlRequestConfirmationPrompt;

  return (
    Boolean(prompt) && typeof prompt === 'object' && !Array.isArray(prompt)
  );
};

export function isHITLRequestConfirmation(
  data: BaseDataMessage,
): data is HITLRequestConfirmationMessage {
  return (
    data.type === ConfirmationMessageTypeEnum.HITLRequestConfirmation ||
    hasHitlRequestPrompt(data)
  );
}

export function parseBaseDataMessage(
  data: unknown,
): BaseDataMessage | undefined {
  const result = BaseDataMessageSchema.safeParse(data);

  if (!result.success) return undefined;

  const message = data as BaseDataMessage;

  if (
    message.type !== ConfirmationMessageTypeEnum.HITLRequestConfirmation &&
    hasHitlRequestPrompt(message)
  ) {
    return {
      ...message,
      type: ConfirmationMessageTypeEnum.HITLRequestConfirmation,
    };
  }

  return message;
}

function getPrimaryHITLDecision(
  confirmation: UserConfirmationMessage,
): HITLRequestConfirmationDecision | undefined {
  return confirmation.decision ?? confirmation.decisions?.[0];
}

function getHITLDecisions(
  confirmation: UserConfirmationMessage,
): HITLDecision[] | undefined {
  return (
    confirmation.decisions ??
    (confirmation.decision ? [confirmation.decision] : undefined)
  );
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => !!value))];
}

export function getHITLTargetIds({
  dataMessage,
  messagePartId,
  actionRequests,
}: {
  dataMessage?: HITLRequestConfirmationMessage;
  messagePartId?: string;
  actionRequests?: HITLActionRequest[];
}): string[] {
  const resolvedActionRequests =
    actionRequests ??
    dataMessage?.hitlRequestConfirmationPrompt?.actionRequests;

  return uniqueStrings([
    dataMessage?.id,
    messagePartId,
    ...(resolvedActionRequests ?? []).flatMap((actionRequest) => [
      actionRequest.id,
      actionRequest.toolCallId,
    ]),
  ]);
}

function confirmationMatchesTargetIds({
  confirmation,
  targetMessageIds,
}: {
  confirmation: UserConfirmationMessage;
  targetMessageIds: string[];
}): boolean {
  return targetMessageIds.some(
    (targetMessageId) =>
      confirmation.subs?.includes(targetMessageId) ||
      (getHITLDecisions(confirmation) ?? []).some(
        (decision) =>
          decision.id === targetMessageId ||
          decision.toolCallId === targetMessageId,
      ),
  );
}

function confirmationMatchesByPosition(
  confirmation: UserConfirmationMessage,
): boolean {
  return (
    (!confirmation.subs || confirmation.subs.length === 0) &&
    !!getPrimaryHITLDecision(confirmation)
  );
}

function readConfirmationScanEntries(
  message: TerraUIMessage,
): ConfirmationScanEntry[] {
  const metadataConfirmation = getMetadataAsUserConfirmation(message.metadata);
  const partEntries = (message.parts ?? []).flatMap(
    (part): ConfirmationScanEntry[] => {
      if (!isDataOrConfirmationPart(part) || !part.data) return [];
      const partData = parseBaseDataMessage(part.data);

      if (!partData) return [];
      if (isHITLRequestConfirmation(partData))
        return [{ kind: 'hitl-request' }];
      if (isUserConfirmationData(partData)) {
        return [{ kind: 'confirmation', confirmation: partData }];
      }

      return [];
    },
  );

  return metadataConfirmation
    ? [
        { kind: 'confirmation', confirmation: metadataConfirmation },
        ...partEntries,
      ]
    : partEntries;
}

function findUserConfirmationById({
  messages,
  startIndex,
  targetMessageIds,
}: ConfirmationSearch): UserConfirmationMessage | undefined {
  const messagesFromStartFirst = [
    ...messages.slice(startIndex),
    ...messages.slice(0, startIndex),
  ];

  for (const message of messagesFromStartFirst) {
    const match = readConfirmationScanEntries(message).find(
      (entry) =>
        entry.kind === 'confirmation' &&
        confirmationMatchesTargetIds({
          confirmation: entry.confirmation,
          targetMessageIds,
        }),
    );

    if (match?.kind === 'confirmation') return match.confirmation;
  }

  return undefined;
}

function findUserConfirmationByPosition({
  messages,
  startIndex,
}: Omit<ConfirmationSearch, 'targetMessageIds'>):
  | UserConfirmationMessage
  | undefined {
  for (const message of messages.slice(startIndex)) {
    const match = readConfirmationScanEntries(message).find(
      (entry) =>
        entry.kind === 'hitl-request' ||
        confirmationMatchesByPosition(entry.confirmation),
    );

    if (match?.kind === 'hitl-request') return undefined;
    if (match) return match.confirmation;
  }

  return undefined;
}

export function findMatchingUserConfirmation({
  messages,
  startIndex,
  targetMessageIds,
}: ConfirmationSearch): UserConfirmationMessage | undefined {
  return targetMessageIds.length > 0
    ? findUserConfirmationById({ messages, startIndex, targetMessageIds })
    : findUserConfirmationByPosition({ messages, startIndex });
}

function resolveHITLDecisionForTarget({
  confirmation,
  targetMessageIds,
}: {
  confirmation: UserConfirmationMessage;
  targetMessageIds: string[];
}): HITLConfirmationDecisionResult {
  const decisions = getHITLDecisions(confirmation);

  if (!decisions || decisions.length === 0) {
    return EMPTY_DECISION_RESULT;
  }

  const matchedDecision = decisions.find(
    (item) =>
      (item.id && targetMessageIds.includes(item.id)) ||
      (item.toolCallId && targetMessageIds.includes(item.toolCallId)),
  );

  return {
    decision: matchedDecision ?? getPrimaryHITLDecision(confirmation),
    decisions,
  };
}

export function findUserConfirmationForHITL(
  search: ConfirmationSearch,
): HITLConfirmationDecisionResult {
  const confirmation = findMatchingUserConfirmation(search);

  return confirmation
    ? resolveHITLDecisionForTarget({
        confirmation,
        targetMessageIds: search.targetMessageIds,
      })
    : EMPTY_DECISION_RESULT;
}

export function findHITLRequestMessageIndex({
  messages,
  targetMessageIds,
}: {
  messages: TerraUIMessage[];
  targetMessageIds: string[];
}): number {
  return messages.findIndex((message) =>
    (message.parts ?? []).some((part) => {
      if (!isDataOrConfirmationPart(part) || !part.data) return false;
      const partData = parseBaseDataMessage(part.data);

      return (
        !!partData &&
        isHITLRequestConfirmation(partData) &&
        getHITLTargetIds({
          dataMessage: partData,
          messagePartId: part.id,
        }).some((id) => targetMessageIds.includes(id))
      );
    }),
  );
}
